import { useEffect, useState } from 'react';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { mediaOutletService } from '@/services/mediaOutletService';
import type { MediaOutlet } from '@/types/mediaOutlets';
import { Loader2, Star } from 'lucide-react';

interface MediaOutletSelectorProps {
  category: 'TV' | 'Radio';
  selectedOutletId: string | null;
  onSelect: (outletId: string) => void;
}

export const MediaOutletSelector: React.FC<MediaOutletSelectorProps> = ({
  category,
  selectedOutletId,
  onSelect
}) => {
  const [outlets, setOutlets] = useState<MediaOutlet[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadOutlets();
  }, [category]);

  const loadOutlets = async () => {
    setLoading(true);
    try {
      const result = await mediaOutletService.getAllOutlets({ category, is_active: true });
      setOutlets(result);
    } catch (error) {
      console.error('Error loading outlets:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (outlets.length === 0) {
    return (
      <div className="text-center py-6 text-muted-foreground">
        No {category === 'TV' ? 'channels' : 'stations'} available
      </div>
    );
  }

  return (
    <RadioGroup
      value={selectedOutletId || ''}
      onValueChange={onSelect}
      className="grid gap-2"
    >
      {outlets.map((outlet) => (
        <div
          key={outlet.id}
          className="flex items-center space-x-2 rounded-lg border border-border p-3 hover:bg-accent/50 cursor-pointer"
        >
          <RadioGroupItem value={outlet.id} id={outlet.id} />
          <Label htmlFor={outlet.id} className="flex items-center gap-2 cursor-pointer flex-1">
            <span className="font-medium">{outlet.outlet_name}</span>
            {outlet.is_popular && (
              <Badge variant="secondary" className="flex items-center gap-1">
                <Star className="w-3 h-3 fill-current" />
                Popular
              </Badge>
            )}
            {outlet.usage_count > 0 && (
              <span className="text-xs text-muted-foreground ml-auto">
                {outlet.usage_count} viewer{outlet.usage_count !== 1 ? 's' : ''}
              </span>
            )}
          </Label>
        </div>
      ))}
    </RadioGroup>
  );
};
